"use client";

import {
  WorkplaceRequestCategory,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { IconTile } from "@gettactic/components/src/data-display/IconTile";
import { type IconValue } from "@gettactic/components/src/icons";
import { Button } from "@gettactic/components/src/primitives/button";
import React from "react";

const parseIconValue = (value?: string | null): IconValue | null => {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator === -1) return null;
  return { set: value.slice(0, separator), icon: value.slice(separator + 1) };
};

interface RequestTypeHeaderProps {
  type: WorkplaceRequestTypeElement;
  category?: WorkplaceRequestCategory;
  /** Returns to the type-picker step so the user can change the selected type. */
  onChangeType?: () => void;
}

/**
 * The "here's what you picked" row at the top of step 2.
 *
 * Shared by the form step and the instructions step rather than duplicated: the
 * two mechanisms are configured differently but are meant to look the same to
 * the person filling them in.
 */
export const RequestTypeHeader: React.FC<RequestTypeHeaderProps> = ({
  type,
  category,
  onChangeType
}) => (
  <div className="mt-4 flex items-center gap-[11px] rounded-[10px] border border-neutral-100 bg-neutral-bg px-[13px] py-[11px]">
    <IconTile
      color={category?.color}
      icon={parseIconValue(type.icon)}
      size="sm"
      bordered
      className="h-[30px] w-[30px] shrink-0"
    />
    <div className="min-w-0 flex-1">
      <h3 className="truncate font-semibold text-[13px] text-neutral-1000 leading-[1.25]">
        {type.name}
      </h3>
      {type.description ? (
        <p className="mt-0.5 line-clamp-1 text-[12px] text-neutral-600 leading-[1.35]">
          {type.description}
        </p>
      ) : null}
    </div>
    {onChangeType ? (
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={onChangeType}
        className="shrink-0"
      >
        Change
      </Button>
    ) : null}
  </div>
);
